// Source de vérité des projets. Tout le contenu provient du CV de Kévin.
// Les champs `process` et `outcome` sont volontairement vides : à remplir par Kévin,
// rien ne doit y être inventé. Les sections correspondantes ne s'affichent que si remplies.

export const projects = [
  {
    slug: 'azrising',
    title: 'AZRising',
    subtitle: 'Application e-sport · Workshop client',
    summary:
      "Conception UX/UI d'une application de gestion de budget et de dépenses pour équipes e-sport.",
    description:
      "AZ Rising est une association e-sport féminine nantaise, fondée début 2026 et spécialisée sur Valorant. Elle participe à des LAN et organise des bootcamps, qui impliquent trajets, hébergements et défraiements. Faute d'outil de suivi, tout vivait dans des Google Sheets non centralisés : les achats du président n'étaient pas systématiquement déclarés au trésorier, et la traçabilité s'évaporait. J'ai conçu l'application qui manquait — centraliser les dépenses, les suivre par événement et prévoir les budgets, pour trois profils aux besoins opposés.",
    image: '/projects/AZRising-KG.webp',
    tags: ['Figma', 'UX/UI Design', 'Design system', 'Dashboard'],
    year: '2026',
    client: 'AZ Rising · Workshop client MBA',
    role: 'UX/UI Design · Recherche · Design system · Prototype',
    kind: 'figma',
    links: [
      {
        href: 'https://www.figma.com/design/N6AcQjZOsIQ0dZU4v62RyT/Maquette?node-id=581-7&t=rEuB4mFbGKus5LK3-1',
        label: 'Voir la maquette',
        primary: true
      }
    ],
    highlights: [
      'Trois profils aux besoins opposés : saisie mobile sur le terrain pour le président, analyse et validation sur desktop pour le trésorier, consultation pour les joueuses',
      'Sept applications comparées sur six critères pondérés — aucune ne réunissait gratuité, rôles associatifs, suivi des déplacements et personnalisation',
      'Dix-sept user stories priorisées en MoSCoW, rattachées à trois parcours utilisateurs',
      "Design system complet en quatre pages, variables organisées selon la nomenclature shadcn/UI",
      'Contrastes tenus à 4,5:1, cibles tactiles à 48 px, une seule famille d’icônes',
      'Projet mené à budget zéro, du cadrage au prototype, en deux mois'
    ],
    process: [
      {
        title: 'Cadrage',
        text: "Entretien initial avec le président, puis note de cadrage validée avec lui. Le périmètre est tranché dès le départ : le suivi des dépenses pendant les LAN et les bootcamps — trajets, logements, défraiements — à l'exclusion des compétitions en ligne, du back-office et de tout développement. Suivi du projet sur Notion, avec un registre des risques tenu à jour et un point client à chaque livrable."
      },
      {
        title: 'Recherche',
        text: "Entretiens semi-directifs avec le président puis le trésorier. En parallèle, benchmark de sept applications réparties en trois familles — dépenses partagées, notes de frais professionnelles, suivi budgétaire associatif — chacune notée sur six critères pondérés. Conclusion : aucune ne réunit la gratuité, des rôles adaptés à une association e-sport, le suivi des déplacements et la personnalisation visuelle. Le besoin n'est pas couvert par le marché."
      },
      {
        title: 'Une donnée manquante, assumée plutôt que masquée',
        text: "Je prévoyais d'interroger une joueuse en plus des deux dirigeants. Président et trésorier ont tranché : les joueuses n'ayant qu'un accès en consultation, l'entretien n'aurait pas lieu. Plutôt que d'inventer ce profil, j'ai construit son persona à partir de sources secondaires uniquement, en annotant chaque élément par sa provenance — entretien direct, croisement de sources, ou source secondaire. Son parcours a été marqué comme conditionnel à une validation client, et la lacune inscrite au registre des risques. Une persona non vérifiée qui s'ignore oriente les décisions sans qu'on s'en aperçoive ; signalée, elle reste exploitable."
      },
      {
        title: 'Structure avant esthétique',
        text: "Trois parcours utilisateurs et dix-sept user stories priorisées en MoSCoW, chacune assortie de ses critères d'acceptation. Puis des wireframes en noir et blanc, déclinés en desktop et en mobile, pour trancher l'agencement sans qu'une question visuelle vienne brouiller le jugement."
      },
      {
        title: 'Design system puis maquettes',
        text: "Un système de design en quatre pages — fondations, composants de base, composants métier, navigation — avec des variables organisées selon la nomenclature shadcn/UI, de façon à ce que les maquettes soient directement transposables en code. Thème sombre exclusif et sans dégradé, conformément à la charte de l'association. Contrastes tenus à 4,5:1, cibles tactiles à 48 px minimum, une seule famille d'icônes."
      },
      {
        title: 'Prototype',
        text: "Prototype interactif couvrant les parcours critiques des trois profils : saisie d'une dépense en mobilité côté président, tableau de bord, validation et export CSV côté trésorier, consultation de ses propres dépenses côté joueuse. Navigable en conditions réelles, en desktop comme en mobile."
      }
    ],
    outcome:
      "Prototype livré et présenté au client en réunion de clôture, avec des retours corrects. Le projet en est resté là : aucune suite n'a été donnée, l'application n'a pas été développée. Les tests utilisateurs, planifiés après la remise du dossier, n'ont pas eu lieu — le prototype n'a donc jamais été confronté à ses utilisateurs, et c'est la limite de ce travail."
  },
  {
    slug: 'elysion',
    title: 'Elysion',
    subtitle: "Plateforme SaaS · Projet de fin d'études",
    summary:
      "Conception UX/UI d'une plateforme de simulation et de planification de retraite.",
    description:
      "Pour les freelances, les dirigeants et même les salariés, la retraite reste un sujet flou qu'on repousse. Deux difficultés se superposent : comprendre sa situation, et retrouver des documents éparpillés entre caisses, URSSAF et bulletins. Elysion réunit un simulateur personnalisé selon le statut et un coffre-fort numérique. Projet de fin d'études mené sur deux ans par une équipe de six filières différentes, où j'étais le seul profil UX/UI.",
    image: '/projects/Elysion-KG.webp',
    tags: ['Figma', 'UX/UI Design', 'Design system', 'shadcn/ui', 'SaaS'],
    year: '2024 – 2026',
    client: "MyDigitalSchool · Projet de fin d'études",
    role: 'Recherche utilisateur · UX/UI Design · Design system',
    kind: 'figma',
    links: [
      {
        href: 'https://www.figma.com/design/ooCNKpCZ5kMUGOGnsZQcP1/Elysion-V3?node-id=34-1361&t=rXGz9obQ91uXDtOO-1',
        label: 'Voir la maquette',
        primary: false
      },
      {
        href: 'https://elysion-frontend.vercel.app/',
        label: 'Visiter le site',
        primary: true
      }
    ],
    highlights: [
      "Seul profil UX/UI d'une équipe de six : direction artistique, marketing, data et développement",
      'Questionnaire auprès des trois cibles : 60 % ne comprennent pas leur régime de retraite, 73 % s’informent via des documents épars, 86,7 % placent la simplicité avant tout',
      'Trois parcours modélisés : simuler sa retraite, l’optimiser, archiver ses documents',
      'Design system Figma aligné sur shadcn/ui, pour que les maquettes se transposent directement en code',
      '72,7 % du trafic sur mobile : responsive complet, navigation mobile dédiée',
      'Deux ans de projet, de la première hypothèse à la mise en ligne'
    ],
    process: [
      {
        title: 'Partir d’hypothèses, pas d’intuitions',
        text: "Avant toute maquette, j'ai formulé trois hypothèses de recherche : les utilisateurs manquent d'un endroit sûr où regrouper leurs documents ; ils ne comprennent pas leur situation administrative ; ils veulent simuler des scénarios mais trouvent les outils existants trop sommaires. Un questionnaire diffusé aux trois cibles — freelances, salariés, dirigeants — les a confirmées. La data analyste de l'équipe en a fait le traitement statistique, que j'ai traduit en priorités d'interface."
      },
      {
        title: 'Trois statuts, trois parcours',
        text: "Un freelance aux revenus irréguliers, une salariée qui veut mesurer l'effet d'un congé, un dirigeant à cheval sur plusieurs caisses : les trois n'ont ni les mêmes questions ni le même vocabulaire. Plutôt qu'un parcours unique, j'ai modélisé trois trajets — simuler, optimiser, archiver — puis structuré les écrans en wireframes avant de toucher à l'esthétique."
      },
      {
        title: 'Changer d’outil en cours de route',
        text: "J'avais adopté Relume en première année pour produire les wireframes vite. En seconde année, au moment de passer à une production sérieuse, ses structures se sont révélées rigides et leur reprise dans Figma coûtait plus de temps qu'elle n'en faisait gagner. J'ai proposé à l'équipe de basculer vers Emergent, mieux accordé à la stack du développeur. Abandonner un outil qu'on maîtrise n'est pas un aveu d'échec : c'est accepter de réévaluer ce qui sert vraiment le projet."
      },
      {
        title: 'Faire parler le design et le code la même langue',
        text: "C'est la décision dont je suis le plus satisfait. J'ai identifié shadcn/ui comme moyen d'adosser ma bibliothèque de composants Figma à un référentiel concret ; le développeur travaillant en React, Tailwind s'est imposé dans la foulée. Le design system a été reconstruit sur cette base. Le coût a été réel — il a fallu reprendre des livrables déjà produits — mais les allers-retours entre maquette et intégration se sont effondrés, parce que nous désignions enfin les mêmes composants par les mêmes noms."
      },
      {
        title: 'Ce qui a manqué',
        text: "La trame d'entretiens qualitatifs était écrite dès la première année. Elle n'a jamais été jouée : repoussée, puis abandonnée faute de temps. Mes arbitrages de conception reposent donc sur une trentaine de réponses quantitatives, utiles pour hiérarchiser mais insuffisantes pour comprendre les usages en profondeur. C'est le regret le plus net que je garde de ce projet, et la première chose que je planifierais autrement."
      }
    ],
    outcome:
      "La plateforme est en ligne : plus de dix pages, simulateurs salarié et freelance, coffre-fort documentaire, sauvegarde automatique des simulations. Le simulateur freelance a été entièrement reconstruit avec les barèmes 2026, après que sa logique de calcul se soit révélée bancale — un coût assumé collectivement au nom de la fiabilité. Aucun test utilisateur n'a en revanche été mené : la plateforme fonctionne, mais elle n'a pas encore été confrontée à ses utilisateurs."
  },
  {
    slug: 'ufab49',
    title: 'UFAB49',
    subtitle: 'Club de basket · Angers',
    summary:
      "Site officiel du club de basket UFAB49, de l'effectif à la billetterie.",
    description:
      "Site officiel du club de basket UFAB49. Présentation de l'effectif, classements et résultats en temps réel. Accès direct à la billetterie et à la boutique WooCommerce pour une expérience supporter complète.",
    image: '/projects/ufab49-KG.webp',
    tags: ['WordPress', 'WooCommerce', 'Responsive'],
    year: '2024',
    client: 'UFAB49',
    role: 'Intégration WordPress · Mission freelance',
    kind: 'web',
    links: [{ href: 'https://www.ufab49.com/', label: 'Visiter le site', primary: true }],
    highlights: [
      "Présentation de l'effectif, classements et résultats",
      'Billetterie et boutique WooCommerce intégrées',
      'Reconstruit intégralement en local avant toute bascule en ligne'
    ],
    process: [
      {
        title: 'Audit de l’existant',
        text: "Avant de toucher quoi que ce soit, je relève ce qui tient et ce qui coince : structure des pages, extensions réellement utiles, contenus à conserver. Sur un site de club, l'enjeu est que le supporter trouve vite l'essentiel — l'effectif, le prochain match, la billetterie — sans se perdre dans l'arborescence."
      },
      {
        title: 'Reconstruction en local',
        text: "Je rebâtis le site dans un environnement local avec LocalWP, qui me sert de préproduction. Tout s'y construit et s'y teste — mise en page, responsive, boutique WooCommerce — pendant que le site en ligne continue de tourner sans être perturbé."
      },
      {
        title: 'Migration',
        text: "Une fois la version locale validée, je migre vers l'hébergement. Travailler ainsi évite l'erreur la plus coûteuse du WordPress : modifier directement en production et casser un site devant ses visiteurs."
      }
    ],
    outcome:
      "Site en ligne et toujours en service, avec billetterie et boutique accessibles aux supporters."
  },
  {
    slug: 'ipfocus',
    title: 'IPFocus',
    subtitle: 'Opérateur télécom B2B',
    summary: "Refonte complète du site d'un opérateur télécom et réseaux.",
    description:
      "Vitrine B2B pour un opérateur téléphonique - télécom réseaux. Présentation des services, mobile et cybersécurité, du matériel fourni aux clients, et affichage clair des tarifs.",
    image: '/projects/ipfocus-KG.webp',
    tags: ['WordPress', 'Responsive', 'B2B'],
    year: '2024',
    client: 'IPFocus',
    role: 'Intégration front-end · Stage',
    kind: 'web',
    links: [{ href: 'https://ipfocus-reseaux.fr/', label: 'Visiter le site', primary: true }],
    highlights: [
      'Services mobile, télécom et cybersécurité',
      'Présentation du matériel et affichage clair des tarifs',
      'Refonte complète, reconstruite en local avant mise en ligne'
    ],
    process: [
      {
        title: 'Audit de l’existant',
        text: "Relevé de ce qui fonctionne et de ce qui dessert le site : architecture, extensions, contenus à reprendre. Pour un opérateur télécom en B2B, la difficulté est de rendre lisible une offre technique — services, matériel fourni, tarifs — à des décideurs qui ne sont pas ingénieurs."
      },
      {
        title: 'Reconstruction en local',
        text: "Le site est rebâti dans un environnement local avec LocalWP, qui tient lieu de préproduction. J'y construis et teste l'ensemble — structure, mise en page, responsive — sans jamais toucher au site que les clients consultent."
      },
      {
        title: 'Migration',
        text: "Bascule vers l'hébergement une fois la version locale validée. C'est ce qui permet de livrer une refonte complète sans fenêtre de casse visible par les visiteurs."
      }
    ],
    outcome: 'Site refondu et en ligne, toujours en service.'
  },
  {
    slug: 'station-c',
    title: 'Station C',
    subtitle: 'Espace de coworking',
    summary: "Site vitrine d'un espace de coworking et de ses événements.",
    description:
      "Site internet d'un espace de coworking. Présentation des espaces et avantages pour les professionnels, liste des événements et ateliers organisés au sein de l'espace.",
    image: '/projects/stationc-KG.webp',
    tags: ['WordPress', 'Responsive'],
    year: '2023',
    client: 'Station C',
    role: 'Intégration WordPress · Stage',
    kind: 'web',
    links: [{ href: 'https://www.stationc.co/', label: 'Visiter le site', primary: true }],
    highlights: [
      'Présentation des espaces et des avantages professionnels',
      'Agenda des événements et ateliers',
      'Reconstruit en local avant mise en ligne'
    ],
    process: [
      {
        title: 'Audit de l’existant',
        text: "Inventaire de ce qui mérite d'être gardé et de ce qui freine : structure, extensions, contenus. Pour un espace de coworking, deux choses comptent avant tout — donner envie de venir voir les lieux, et tenir à jour un agenda d'événements sans que ce soit une corvée."
      },
      {
        title: 'Reconstruction en local',
        text: "Reconstruction complète dans un environnement local avec LocalWP, utilisé comme préproduction : la mise en page et le responsive s'y valident tranquillement, pendant que le site public reste intact."
      },
      {
        title: 'Migration',
        text: "Mise en ligne une fois la version locale approuvée, sans intervention hasardeuse sur le site en production."
      }
    ],
    outcome: 'Site en ligne, avec son agenda et la présentation des espaces.'
  },
  {
    slug: 'sublim-home',
    title: 'Sublim&Home',
    subtitle: 'Entreprise du bâtiment',
    summary: "Refonte du site vitrine d'une entreprise de rénovation.",
    description:
      "Vitrine d'une entreprise de rénovation. Présentation des compétences d'intervention (gros œuvre, second œuvre, finitions) et mise en avant des réalisations clients.",
    image: '/projects/SublimHome-KG.webp',
    tags: ['WordPress', 'Responsive', 'Artisan'],
    year: '2022',
    client: 'Sublim&Home',
    role: 'Intégration WordPress · Stage',
    kind: 'web',
    links: [{ href: 'https://www.sublimethome.fr/', label: 'Visiter le site', primary: true }],
    highlights: [
      "Compétences d'intervention : gros œuvre, second œuvre, finitions",
      'Mise en avant des réalisations clients',
      'Reconstruit en local avant mise en ligne'
    ],
    process: [
      {
        title: 'Audit de l’existant',
        text: "Examen de ce qui est en place avant d'y toucher : structure, extensions, contenus réutilisables. Pour une entreprise de rénovation, ce qui convainc n'est pas le discours mais les chantiers déjà livrés — la refonte s'est organisée autour de la mise en valeur des réalisations."
      },
      {
        title: 'Reconstruction en local',
        text: "Le site est rebâti dans un environnement local avec LocalWP, qui fait office de préproduction : tout se construit et se vérifie hors ligne, le site public n'est pas perturbé pendant les travaux."
      },
      {
        title: 'Migration',
        text: "Bascule vers l'hébergement une fois la version locale validée."
      }
    ],
    outcome:
      "Site vitrine refondu et mis en ligne, organisé autour des compétences et des réalisations de l'entreprise."
  }
];

export const getProject = (slug) => projects.find((p) => p.slug === slug);

export const getNextProject = (slug) => {
  const i = projects.findIndex((p) => p.slug === slug);
  return projects[(i + 1) % projects.length];
};
